export interface ReleaseSeoOffer {
  offerCount: number;
  lowPrice: number;
  highPrice: number;
  priceCurrency: string;
}

interface ReleaseSeoOfferInput {
  offers?: Array<{
    currentPriceTotal: string | null;
    currentCurrency: string | null;
  }> | null;
}

/**
 * Builds a stable AggregateOffer projection from the release page's prefetched offers.
 * Google's Product snippets expect both `lowPrice` and `highPrice`, so a single price
 * is published as a degenerate range.
 */
export function buildReleaseSeoOffer(input: ReleaseSeoOfferInput): ReleaseSeoOffer | null {
  const pricedOffers = (input.offers ?? [])
    .map((offer) => ({
      currency: offer.currentCurrency,
      price: offer.currentPriceTotal ? Number(offer.currentPriceTotal) : Number.NaN,
    }))
    .filter(
      (offer): offer is { currency: string; price: number } =>
        Boolean(offer.currency) && Number.isFinite(offer.price) && offer.price >= 0,
    );
  const priceCurrency = pricedOffers[0]?.currency;
  if (!priceCurrency) return null;

  const prices = pricedOffers
    .filter((offer) => offer.currency === priceCurrency)
    .map((offer) => offer.price);
  if (prices.length === 0) return null;

  return {
    offerCount: prices.length,
    lowPrice: Math.min(...prices),
    highPrice: Math.max(...prices),
    priceCurrency,
  };
}
